import { useEffect, useRef, useState } from 'react';

/**
 * A hook for managing hover-to-open dropdown behavior with smooth transitions.
 *
 * ## When to Use
 *
 * Use this hook when you need a dropdown that:
 * - Opens when the user hovers over a trigger element
 * - Remains open when moving the cursor from trigger to dropdown content
 * - Closes only after the cursor leaves both trigger and content for a specified delay
 *
 * Common use cases:
 * - Action menus that should feel responsive to hover
 * - Navigation dropdowns
 * - Context menus with hover activation
 * - Any dropdown where you want to avoid accidental closures during cursor movement
 *
 * ## Why Use This Hook
 *
 * Without a delay mechanism, dropdowns can feel janky and frustrating:
 * - The dropdown closes immediately when the cursor leaves the trigger
 * - Users must move their cursor perfectly from trigger to content without any gap
 * - Even small gaps between trigger and content make the dropdown unusable
 *
 * @param options - Configuration options for the hover behavior
 * @returns An object containing state and handlers for managing the dropdown
 *
 * @example
 * ```tsx
 * const MyDropdown = () => {
 *   const {
 *     isOpen,
 *     setIsOpen,
 *     handleOpen,
 *     handleClose,
 *   } = useDropdownOnHover({ closeDelay: 200 });
 *
 *   return (
 *     <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
 *       <DropdownMenuTrigger>
 *         <div onMouseEnter={handleOpen} onMouseLeave={handleClose}>
 *           <MoreVertical />
 *         </div>
 *       </DropdownMenuTrigger>
 *       <DropdownMenuContent
 *         onMouseEnter={handleOpen}
 *         onMouseLeave={handleClose}
 *       >
 *         <DropdownMenuItem>Action 1</DropdownMenuItem>
 *         <DropdownMenuItem>Action 2</DropdownMenuItem>
 *       </DropdownMenuContent>
 *     </DropdownMenu>
 *   );
 * };
 * ```
 */
export function useDropdownOnHover() {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleOpen = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  return {
    isOpen,
    setIsOpen,
    handleOpen,
    handleClose,
  };
}
